import SearchBar from "@/ui-components/search-bar";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useSearchStocksRatings } from "./hooks/useSearchStocksRatings";
import { FirehoseHighlightItem } from "../firehose-all/components/FirehoseHighlightItem";
import React, { useEffect, useMemo, useState } from "react";
import { GroupedVirtuoso } from "react-virtuoso";
import { NewsFeedItem } from "@/shared/api/firehose/models";
import toast from "react-hot-toast";
import * as Sentry from "@sentry/react";
import { CommonSearchProps } from "@/widget-components/newsquawk-headlines/HeadlinesSearchForm";
import { LoadMoreButton } from "@/ui-components/load-more-button";
import EmptyPlaceholder from "@/ui-components/empty-placeholder";
import { DateSectionHeader } from "../shared/DateSectionHeader";

function groupFirehoseByDate(items: NewsFeedItem[]) {
  const grouped = items.reduce(
    (acc, item) => {
      const date = new Date(item.created_utc);
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      let dateKey: string;
      if (date.toDateString() === today.toDateString()) {
        dateKey = "Today";
      } else if (date.toDateString() === yesterday.toDateString()) {
        dateKey = "Yesterday";
      } else {
        dateKey = date.toLocaleDateString("en-US", {
          weekday: "long",
          month: "short",
          day: "numeric",
        });
      }

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(item);
      return acc;
    },
    {} as Record<string, NewsFeedItem[]>
  );

  const groups = Object.keys(grouped).sort((a, b) => {
    if (a === "Today") return -1;
    if (b === "Today") return 1;
    if (a === "Yesterday") return -1;
    if (b === "Yesterday") return 1;
    return new Date(b).getTime() - new Date(a).getTime();
  });

  const groupedCounts = groups.map((group) => grouped[group].length);
  const flatItems = groups.flatMap((group) => grouped[group]);

  return { groups, groupedCounts, items: flatItems };
}

function StocksRatingsSearchForm(props: CommonSearchProps) {
  const {
    isLoading,
    isSuccess,
    isError,
    searchStocksRatingsData,
    searchResults,
    clearSearchResults,
    loadNextPage,
    hasMore,
  } = useSearchStocksRatings();

  const { groups, groupedCounts, items } = useMemo(
    () => groupFirehoseByDate(searchResults),
    [searchResults]
  );

  const { setTitle } = props;
  const [query, setQuery] = useState<string>("");

  const [startDate, setStartDate] = React.useState<string>("");
  const [startTime, setStartTime] = React.useState<string>("00:00");
  const [endDate, setEndDate] = React.useState<string>("");
  const [endTime, setEndTime] = React.useState<string>("23:59");

  const formRef = React.useRef<HTMLDivElement>(null);

  const [formHeight, setFormHeight] = React.useState<number>(40);

  const showSearchResults = searchResults.length > 0 || isSuccess;

  const handleSearch = (query: string) => {
    clearSearchResults();

    setQuery(query);

    // Convert date strings to ISO strings with time
    let startDateISO: string | undefined;
    let endDateISO: string | undefined;

    if (startDate) {
      const [h, m] = (startTime || "00:00").split(":").map(Number);
      const startDateObj = new Date(startDate);
      startDateObj.setHours(h, m, 0, 0);
      startDateISO = startDateObj.toISOString();
    }

    if (endDate) {
      const [h, m] = (endTime || "23:59").split(":").map(Number);
      const endDateObj = new Date(endDate);
      endDateObj.setHours(h, m, 59, 999);
      endDateISO = endDateObj.toISOString();
    }

    searchStocksRatingsData({
      q: query,
      limit: 50,
      date_range: { start_date: startDateISO, end_date: endDateISO },
    });
  };

  const handleClear = () => {
    setQuery("");
    setStartDate("");
    setEndDate("");
    clearSearchResults();
  };

  const footer = hasMore ? (
    <LoadMoreButton loading={isLoading} loadNextPage={loadNextPage} />
  ) : (
    <div className="flex justify-center py-4 text-gray-400 text-sm">
      End of results
    </div>
  );

  useEffect(() => {
    if (!isError) return;
    toast.error(
      "There was an error fetching the stocks ratings stories, please try again."
    );
    Sentry.captureMessage("Error fetching stocks ratings stories in search");
  }, [isError]);

  useEffect(() => {
    const titleString = `Showing results for '${query}'`;

    if (showSearchResults) {
      setTitle?.(titleString);
    } else {
      setTitle?.("Broker Ratings");
    }

    return () => setTitle?.("Broker Ratings");
  }, [showSearchResults, query, setTitle]);

  // Clear search results when the component unmounts
  useEffect(() => clearSearchResults, [clearSearchResults]);

  // Measure and update form height dynamically
  useEffect(() => {
    const updateFormHeight = () => {
      if (formRef.current) {
        const height = formRef.current.offsetHeight;
        setFormHeight(height);
      }
    };

    // Initial measurement
    updateFormHeight();

    // Set up ResizeObserver to handle dynamic height changes
    const resizeObserver = new ResizeObserver(updateFormHeight);
    if (formRef.current) {
      resizeObserver.observe(formRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <React.Fragment>
      <div
        className="bg-gray-800 border-b border-gray-700/50 px-3 py-2 space-y-2"
        ref={formRef}
      >
        {/* Search bar + close */}
        <div className="flex items-center gap-2">
          <SearchBar
            loading={isLoading}
            onSearch={handleSearch}
            onClear={handleClear}
          />
          {props.onClose && (
            <button
              onClick={props.onClose}
              className="p-1 text-gray-400 hover:text-gray-200 transition-colors flex-shrink-0"
            >
              <XMarkIcon className="size-4" />
            </button>
          )}
        </div>

        {/* Date/time range */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">From:</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">To:</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
          </div>

          {props.onClose && (
            <button
              onClick={props.onClose}
              className="ml-auto px-3 py-1 text-xs font-medium rounded bg-squawk-blue hover:bg-squawk-blue/80 text-white transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>

      {showSearchResults ? (
        <section
          className="absolute flex-1 z-30 bg-gray-900 inset-0 pb-[22px] overflow-y-auto scrollbar-thin"
          style={{ top: `${formHeight}px` }}
        >
          <GroupedVirtuoso
            className="scrollbar-thin absolute inset-x-0 top-0"
            groupCounts={groupedCounts}
            groupContent={(index) =>
              groups[index] === "Today" ? (
                <p className="h-[1px]" />
              ) : (
                <DateSectionHeader title={groups[index]} />
              )
            }
            itemContent={(idx) => (
              <FirehoseHighlightItem key={items[idx].id} item={items[idx]} />
            )}
            components={{
              Footer: () => footer,
            }}
          />
        </section>
      ) : query && !isLoading ? (
        <section
          className="absolute flex-1 z-30 bg-gray-900 inset-0 pb-[40px] overflow-y-auto scrollbar-thin"
          style={{ top: `${formHeight}px` }}
        >
          <EmptyPlaceholder
            message={"No stocks ratings stories found"}
            isLoading={false}
          />
        </section>
      ) : null}
    </React.Fragment>
  );
}

export default StocksRatingsSearchForm;
